import { useAppSelector } from '@/hooks';
import { FilterFunctionCall, getUsedFieldsFromFilter } from './FormularTree';
import { useMemo } from 'react';
import { GetIsDevMode } from '@/forms/shared/IsDevMode';

let totalCalls = 0;
let totalRuns = 0;
let cacheHits = 0;

const cacheByFilter = new Map<
  string,
  { lastA: RootState | null; lastB: RootState | null; lastResult: boolean | null }
>();

window['getUseMemoStateFromFilterDepsCache'] = () => cacheByFilter;

const groupFunctionCallParams = (ungroupedUsedFieldList: FilterFunctionCall[]): FilterFunctionCall[] => {
  const groups = ungroupedUsedFieldList.reduce((acc, curr) => {
    if (!acc.has(curr.funcName)) acc.set(curr.funcName, new Set<string>());

    const targetSet = acc.get(curr.funcName);
    curr.args.forEach((arg) => targetSet.add(arg));

    return acc;
  }, new Map<string, Set<string>>());

  return Array.from(groups.keys()).map(
    (funcName) =>
      ({
        funcName,
        args: Array.from(groups.get(funcName).values()).sort(),
      }) as FilterFunctionCall
  );
};

/**
 * React-Hook, der den gesamten Redux-State (RootState) zurückgibt,
 * aber nur dann ein Re-Render auslöst, wenn sich relevante Felder ändern,
 * die im übergebenen Filter-Ausdruck verwendet werden.
 *
 * Der Filter wird analysiert, um die genutzten Felder zu bestimmen.
 * Nur Änderungen an diesen Feldern (z.B. userData, bikes, deviceMode, customProducts, ensfields, voucher)
 * führen dazu, dass der selektierte State als "geändert" betrachtet wird.
 *
 * Dies optimiert Komponenten, die nur auf bestimmte Teile des States reagieren sollen,
 * ohne dass sie bei jeder kleinen State-Änderung neu gerendert werden.
 *
 * @param {string} filter - Der FrontendFilter, der die zu beobachtenden Felder beschreibt
 * @returns {RootState} - Der aktuelle Redux-State
 */
export const useMemoStateFromFilterDeps = (filter: string): RootState => {
  const calledFuncs = useMemo(() => getUsedFieldsFromFilter(filter), [filter]);

  const groupedCalledFuncs = useMemo(() => groupFunctionCallParams(calledFuncs), [calledFuncs]);
  const requireCustomProductWatch =
    filter.includes('customProductHasAny(') || filter.includes('customProductSelected(');

  const cacheKey = useMemo(
    () =>
      groupedCalledFuncs
        .sort((a, b) => a.funcName.localeCompare(b.funcName))
        .map(
          (a) =>
            `${a.funcName}(` +
            a.args
              .sort()
              .map((arg) => `"${arg}"`)
              .join(',') +
            ')'
        )
        .join('-') + (requireCustomProductWatch ? '-cpw' : ''),
    [groupedCalledFuncs, requireCustomProductWatch]
  );

  const state = useAppSelector(
    (state) => state,
    (a: RootState, b: RootState) => {
      totalCalls++;

      let currentCache = cacheByFilter.get(cacheKey);
      if (!currentCache) {
        currentCache = { lastA: null, lastB: null, lastResult: null };
        cacheByFilter.set(cacheKey, currentCache);
      }

      if (
        (currentCache.lastA === a && currentCache.lastB === b) ||
        (currentCache.lastA === b && currentCache.lastB === a)
      ) {
        cacheHits++;
        return currentCache.lastResult;
      }

      totalRuns++;

      const updateCache = (result: boolean) => {
        currentCache.lastA = a;
        currentCache.lastB = b;
        currentCache.lastResult = result;
        return result;
      };

      for (const calledFunc of calledFuncs) {
        if (calledFunc.funcName.toLocaleLowerCase() === 'prop_req') {
          for (const arg of calledFunc.args) {
            if (a.userData[arg] !== b.userData[arg]) return updateCache(false);
          }
        } else if (calledFunc.funcName.toLocaleLowerCase() === 'prop_obj') {
          const aObjectKeys = Object.keys(a.userData.bikes);
          const bObjectKeys = Object.keys(b.userData.bikes);

          if (!aObjectKeys.every((v, i) => v === bObjectKeys[i])) return updateCache(false);

          const objectKeys = new Set([...Object.keys(a.userData.bikes), ...Object.keys(b.userData.bikes)]);
          for (const bikeKey of objectKeys) {
            for (const arg of calledFunc.args) {
              if (a.userData.bikes?.[bikeKey]?.[arg] !== b.userData.bikes?.[bikeKey]?.[arg]) return false;
            }
          }
        } else if (calledFunc.funcName.toLocaleLowerCase() === 'devicemode') {
          if (
            (a.userData.deviceModeOverride || a.devicemode.devkey) !==
            (b.userData.deviceModeOverride || b.devicemode.devkey)
          )
            return updateCache(false);
        } else if (calledFunc.funcName.toLocaleLowerCase() === 'filter') {
          const valueSelector = (state: RootState) => state.customFilter.filterValues[calledFunc.args[0]];
          if (valueSelector(a) !== valueSelector(b)) return updateCache(false);
        }
      }

      if (requireCustomProductWatch) {
        if (a.customProducs.productSelection !== b.customProducs.productSelection) return updateCache(false);
      }

      if (a.voucher?.voucher?.usableForCashback !== b.voucher?.voucher?.usableForCashback) return updateCache(false);

      if (a.ensfields !== b.ensfields) return updateCache(false);

      return updateCache(true);
    }
  );

  return state;
};
